"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.genericInputParamSchema = exports.inputParamSchema = exports.referenceInputParamSchema = exports.fileInputParamSchema = exports.enumInputParamSchema = exports.anyInputParamSchema = exports.intArrayInputParamSchema = exports.arrayInputParamSchema = exports.stringArrayInputParamSchema = exports.booleanInputParamSchema = exports.floatInputParamSchema = exports.integerInputParamSchema = exports.stringResourceInputParamSchema = exports.stringInputParamSchema = exports.paramTypeToJoiSchemaMap = exports.inputSourcesSchema = void 0;
exports.buildInputParamSchema = buildInputParamSchema;
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
const joi_1 = __importDefault(require("joi"));
const Types_1 = require("./Types");
/**
 * A schema to validate that the input source is one of the allowed values.
 * The validated value will be typed as `InputSource`.
 */
exports.inputSourcesSchema = joi_1.default.string().valid(...Object.keys(Types_1.inputSources));
/**
 * A mapping from a parameter type string to its corresponding Joi validation schema.
 * This is used by the builder function to automatically select the correct schema.
 */
exports.paramTypeToJoiSchemaMap = {
    ANY: joi_1.default.any(),
    STRING: joi_1.default.string(),
    ENUM: joi_1.default.string(),
    FILE: joi_1.default.string().uri(),
    INT: joi_1.default.number().integer(),
    FLOAT: joi_1.default.number(),
    BOOLEAN: joi_1.default.boolean(),
    ARRAY: joi_1.default.array(),
    STRING_ARRAY: joi_1.default.array().items(joi_1.default.string()),
    INT_ARRAY: joi_1.default.array().items(joi_1.default.number().integer()),
    STRING_RES: joi_1.default.object().pattern(joi_1.default.string(), joi_1.default.string()),
};
/**
 * A builder function to generate Joi schemas for types that extend BaseInputParam.
 *
 * @template T The type that represents a valid input param
 *
 * @param type The literal string for the 'type' field (e.g., 'STRING').
 * @param extraKeys An optional Joi object schema for additional fields (e.g., 'options' for ENUM).
 * @returns A Joi alternatives schema strongly typed with `T`.
 */
function buildInputParamSchema(type, extraKeys = joi_1.default.object({})) {
    const valueSchema = exports.paramTypeToJoiSchemaMap[type];
    if (!valueSchema) {
        throw new Error(`Schema generation failed: No Joi schema mapping found for param type "${type}"`);
    }
    const nonConstantSchema = joi_1.default.object({
        type: joi_1.default.string().valid(type).required(),
        src: exports.inputSourcesSchema.invalid('CONSTANT').required(),
        key: joi_1.default.string().required(),
    }).concat(extraKeys);
    const constantSchema = joi_1.default.object({
        type: joi_1.default.string().valid(type).required(),
        src: joi_1.default.string().valid('CONSTANT').required(),
        value: valueSchema.required(),
    }).concat(extraKeys);
    return joi_1.default.alternatives().try(nonConstantSchema, constantSchema);
}
// --- Schema Definitions using the Automated Builder ---
exports.stringInputParamSchema = buildInputParamSchema('STRING');
exports.stringResourceInputParamSchema = buildInputParamSchema('STRING_RES');
exports.integerInputParamSchema = buildInputParamSchema('INT');
exports.floatInputParamSchema = buildInputParamSchema('FLOAT');
exports.booleanInputParamSchema = buildInputParamSchema('BOOLEAN');
exports.stringArrayInputParamSchema = buildInputParamSchema('STRING_ARRAY');
exports.arrayInputParamSchema = buildInputParamSchema('ARRAY');
exports.intArrayInputParamSchema = buildInputParamSchema('INT_ARRAY');
exports.anyInputParamSchema = buildInputParamSchema('ANY');
// For ENUM, we pass the extra 'options' key schema
exports.enumInputParamSchema = buildInputParamSchema('ENUM', joi_1.default.object({
    options: joi_1.default.object().pattern(joi_1.default.string(), joi_1.default.string()).required(),
}));
// -- Schemas that don't fit the general format -- //
// For file input param, we don't want to validate if the file is a valid URI right away
exports.fileInputParamSchema = joi_1.default.alternatives().try(joi_1.default.object({
    type: joi_1.default.string().valid('FILE').required(),
    src: exports.inputSourcesSchema.invalid('CONSTANT').required(),
    key: joi_1.default.string().required(),
}), joi_1.default.object({
    type: joi_1.default.string().valid('FILE').required(),
    src: joi_1.default.string().valid('CONSTANT').required(),
    value: joi_1.default.string().required(),
}));
/**
 * Validates a {@link ReferenceInputParam} object. This type has a unique
 * structure and does not fit the base builder function.
 */
exports.referenceInputParamSchema = joi_1.default.alternatives().try(joi_1.default.object({
    type: joi_1.default.string().valid('REF').required(),
    src: exports.inputSourcesSchema.invalid('CONSTANT').required(),
    key: joi_1.default.string().required(),
    itype: joi_1.default.string().valid('STRING', 'INT', 'BOOLEAN', 'STRING_ARRAY').required(),
    options: joi_1.default.object().pattern(joi_1.default.string(), joi_1.default.string()).required(),
}), joi_1.default.object({
    type: joi_1.default.string().valid('REF').required(),
    src: joi_1.default.string().valid('CONSTANT').required(),
    value: joi_1.default.string().allow(null).required(),
}));
exports.inputParamSchema = {
    STRING: exports.stringInputParamSchema,
    STRING_RES: exports.stringResourceInputParamSchema,
    INT: exports.integerInputParamSchema,
    FLOAT: exports.floatInputParamSchema,
    BOOLEAN: exports.booleanInputParamSchema,
    FILE: exports.fileInputParamSchema,
    STRING_ARRAY: exports.stringArrayInputParamSchema,
    ARRAY: exports.arrayInputParamSchema,
    INT_ARRAY: exports.intArrayInputParamSchema,
    ANY: exports.anyInputParamSchema,
    ENUM: exports.enumInputParamSchema,
    REF: exports.referenceInputParamSchema,
    SPEC: joi_1.default.object(),
};
/**
 * A single, composite schema that validates against any of the possible
 * input parameter types. The validated value will be typed as {@link GenericInputParam}.
 */
exports.genericInputParamSchema = joi_1.default.alternatives().try(exports.stringInputParamSchema, exports.stringResourceInputParamSchema, exports.integerInputParamSchema, exports.floatInputParamSchema, exports.booleanInputParamSchema, exports.fileInputParamSchema, exports.stringArrayInputParamSchema, exports.arrayInputParamSchema, exports.intArrayInputParamSchema, exports.enumInputParamSchema, exports.anyInputParamSchema, exports.referenceInputParamSchema);
//# sourceMappingURL=Schema.js.map